import { useEffect, useMemo, useState, Fragment } from "react";
import dayjs from "dayjs";
import { periodStart } from "@/lib/format";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Banknote, Loader2, Download, Search, AlertTriangle, History, RotateCcw,
  ChevronDown, ChevronRight, Receipt,
} from "lucide-react";
import { fmtLog } from "@/lib/tz";
import { ReceiptThumb } from "@/components/ReceiptThumb";

const TEAL = "#2f6f6b";
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function fortnightRange(offsetWeeks = 0) {
  const start = periodStart(dayjs(), true).subtract(offsetWeeks, "week");
  return { start: start.format("YYYY-MM-DD"), end: start.add(13, "day").format("YYYY-MM-DD") };
}
function fortnightDefault() { return fortnightRange(0); }
function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function Payroll() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [ids, setIds] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [range, setRange] = useState(fortnightDefault());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const [runs, setRuns] = useState([]);
  const [runsOpen, setRunsOpen] = useState(true);
  const [expanded, setExpanded] = useState(null);      // full run detail
  const [runExpenses, setRunExpenses] = useState(null); // expense claims report for the expanded run
  const [voidRun, setVoidRun] = useState(null);         // run to void
  const [voidEmp, setVoidEmp] = useState(null);         // { run, employee }
  const [reason, setReason] = useState("");

  const load = () => api.payrollSettings().then((d) => {
    setData(d); setSettings(d.settings);
    setIds(Object.fromEntries(d.staff.map((s) => [s.employee || s.name, s.eh_id || ""])));
  });
  const loadRuns = () => api.payrollRuns().then(setRuns).catch(() => {});
  useEffect(() => { load().catch(() => toast.error("Could not load payroll data.")); loadRuns(); }, []);

  const staff = useMemo(() => {
    const t = q.toLowerCase();
    return (data?.staff || []).filter((s) => !t || (s.display_name || "").toLowerCase().includes(t));
  }, [data, q]);

  const saveIds = async () => {
    setSaving(true);
    try { const r = await api.updatePayrollStaffIds(ids); toast.success(`Saved ${r.count} employee IDs.`); load(); }
    catch { toast.error("Save failed."); } finally { setSaving(false); }
  };

  const runPreview = async () => {
    setBusy("preview"); setPreview(null);
    try { setPreview(await api.payrollPreview(range.start, range.end)); }
    catch (e) { toast.error(e.response?.data?.detail || "Preview failed."); }
    finally { setBusy(""); }
  };
  const download = async () => {
    setBusy("export");
    try {
      const blob = await api.payrollExport(range.start, range.end);
      saveBlob(blob, `EH_Timesheet_${range.start}_to_${range.end}.csv`);
      toast.success("Pay run created and CSV downloaded.");
      setPreview(null); loadRuns();
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed."); }
    finally { setBusy(""); }
  };
  const redownload = async (r) => {
    try { const res = await api.payrollRunCsvBlob(r.name); saveBlob(res.data, `EH_Timesheet_${r.date_from}_to_${r.date_to}.csv`); }
    catch (e) { toast.error(e.response?.data?.detail || "Download failed."); }
  };
  const downloadExpenses = async (r) => {
    try { const res = await api.payrollRunExpensesCsvBlob(r.name); saveBlob(res.data, `Expense_Claims_${r.date_from}_to_${r.date_to}.csv`); }
    catch (e) { toast.error(e.response?.data?.detail || "Download failed."); }
  };
  const toggleExpand = async (r) => {
    if (expanded?.name === r.name) { setExpanded(null); return; }
    setRunExpenses(null);
    try { setExpanded(await api.payrollRun(r.name)); }
    catch { toast.error("Could not load run detail."); return; }
    api.payrollRunExpenses(r.name).then(setRunExpenses).catch(() => setRunExpenses({ error: true }));
  };

  const doVoidRun = async () => {
    if (!reason.trim()) return toast.error("A reason is required.");
    try {
      const res = await api.voidPayrollRun(voidRun.name, reason.trim());
      toast.success(`Pay run voided — ${res.reopened.length} shift(s) reopened for re-export.`);
      setVoidRun(null); setReason(""); setExpanded(null); loadRuns();
    } catch (e) { toast.error(e.response?.data?.detail || "Void failed."); }
  };
  const doVoidEmp = async () => {
    if (!reason.trim()) return toast.error("A reason is required.");
    try {
      const res = await api.voidPayrollEmployee(voidEmp.run.name, voidEmp.employee.prof, reason.trim());
      toast.success(`${voidEmp.employee.worker_name} voided — ${res.reopened.length} shift(s) reopened${res.run_voided ? " · whole run now voided" : ""}.`);
      setVoidEmp(null); setReason(""); loadRuns();
      try { setExpanded(await api.payrollRun(voidEmp.run.name)); } catch { /* noop */ }
    } catch (e) { toast.error(e.response?.data?.detail || "Void failed."); }
  };

  if (!settings) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-2.5">
        <Banknote className="h-5 w-5" style={{ color: TEAL }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">Payroll</h1>
      </div>

      {!settings.enabled && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2 text-sm text-amber-800" data-testid="payroll-disabled-note">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Payroll export is currently turned off. An admin can enable it and configure Work Type External IDs in <b>Admin Settings → Payroll Export</b>.</span>
        </div>
      )}

      {/* Export a pay run */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
        <div className="text-sm font-semibold text-slate-800">Export a pay run</div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-slate-500">Quick pick:</span>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setRange(fortnightRange(0)); setPreview(null); }} data-testid="payroll-current-fortnight">Current fortnight</Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setRange(fortnightRange(2)); setPreview(null); }} data-testid="payroll-previous-fortnight">Previous fortnight</Button>
          <span className="text-[10px] text-slate-400">Fri → Thu, aligned to the roster fortnight</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-[11px] font-semibold text-slate-600">Period start</Label>
            <Input type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} className="h-9 text-sm mt-1" data-testid="payroll-start" /></div>
          <div><Label className="text-[11px] font-semibold text-slate-600">Period end</Label>
            <Input type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} className="h-9 text-sm mt-1" data-testid="payroll-end" /></div>
          <Button variant="outline" onClick={runPreview} disabled={busy === "preview"} data-testid="payroll-preview">{busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preview"}</Button>
          <Button onClick={download} disabled={busy === "export" || !settings.enabled} style={{ backgroundColor: TEAL }} data-testid="payroll-download">{busy === "export" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" />Download CSV</>}</Button>
        </div>
        {preview && (
          <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-xs space-y-2" data-testid="payroll-preview-result">
            <div className="flex flex-wrap gap-4">
              <span><b>{preview.shifts_exported}</b> of {preview.shifts_total} approved shifts</span>
              <span><b>{preview.employee_count}</b> employee(s)</span>
              <span><b>{preview.row_count}</b> CSV rows</span>
              {preview.expense_count > 0 && <span><b>{preview.expense_count}</b> expense claim(s) · {money(preview.expense_total)} (not in the CSV)</span>}
              {preview.skipped_exported > 0 && <span className="text-slate-500">{preview.skipped_exported} already exported (skipped)</span>}
            </div>
            {preview.warnings?.length > 0 && (
              <div className="text-amber-700">
                <div className="flex items-center gap-1 font-semibold"><AlertTriangle className="h-3.5 w-3.5" />{preview.warnings.length} warning(s)</div>
                <ul className="list-disc pl-5 mt-1 max-h-28 overflow-auto">{preview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
              </div>
            )}
            {preview.workers?.length > 0 && (
              <div className="overflow-auto" data-testid="payroll-preview-workers">
                <div className="font-semibold text-slate-600 mb-1">Hours per worker</div>
                <table className="text-[11px] w-full"><thead><tr className="text-slate-400 text-left">
                  <th className="pr-2">Worker</th><th className="pr-2">Shifts</th><th className="pr-2 text-right">Worked</th>
                  <th className="pr-2 text-right">Active (sleepover)</th><th className="pr-2 text-right">Paid hours</th>
                  <th className="pr-2 text-right">Sleepover (inactive)</th><th className="pr-2 text-right">KM</th><th className="text-right">Expenses</th></tr></thead>
                  <tbody>{preview.workers.map((w) => (
                    <tr key={w.eh + w.worker_name} className="text-slate-700 tabular-nums">
                      <td className="pr-2">{w.worker_name}</td><td className="pr-2">{w.shift_count}</td>
                      <td className="pr-2 text-right">{w.worked_hours}h</td><td className="pr-2 text-right">{w.active_hours}h</td>
                      <td className="pr-2 text-right font-semibold">{w.paid_hours}h</td><td className="pr-2 text-right text-slate-500">{w.sleepover_hours}h</td>
                      <td className="pr-2 text-right">{w.km}</td><td className="text-right">{w.expenses ? money(w.expenses) : "–"}</td>
                    </tr>))}</tbody></table>
              </div>
            )}
            {preview.sample?.length > 0 && (
              <details className="overflow-auto">
                <summary className="cursor-pointer font-semibold text-slate-600">All CSV rows ({preview.sample.length})</summary>
                <table className="text-[10px] w-full mt-1"><thead><tr className="text-slate-400 text-left"><th className="pr-2">Emp ID</th><th className="pr-2">Shift</th><th className="pr-2">Start</th><th className="pr-2">End</th><th className="pr-2">Units</th><th className="pr-2">Type</th><th className="pr-2">Work Type</th><th>Comments</th></tr></thead>
                  <tbody>{preview.sample.map((r, i) => <tr key={i} className="text-slate-600"><td className="pr-2">{r.eh}</td><td className="pr-2">{r.shift}</td><td className="pr-2">{r.start}</td><td className="pr-2">{r.end}</td><td className="pr-2">{r.units}</td><td className="pr-2">{r.ctype}</td><td className="pr-2">{r.cid}</td><td className="truncate max-w-[220px]">{r.comments}</td></tr>)}</tbody></table>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Pay run history */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="payroll-runs">
        <button onClick={() => setRunsOpen((s) => !s)} className="w-full flex items-center gap-2 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="payroll-runs-toggle">
          <History className="h-4 w-4" style={{ color: TEAL }} /> Pay run history
          <Badge variant="outline" className="text-[10px]">{runs.length}</Badge>
          <span className="ml-auto">{runsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</span>
        </button>
        {runsOpen && (
          <div className="border-t border-slate-100">
            {runs.length === 0 ? (
              <p className="px-5 py-6 text-xs text-slate-400 text-center">No pay runs yet. Download a CSV above to create one.</p>
            ) : (
              <table className="w-full text-xs" data-testid="payroll-runs-list">
                <thead><tr className="text-left text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="px-5 py-2">Period</th><th className="py-2">Employees</th><th className="py-2">Shifts</th><th className="py-2">Rows</th><th className="py-2">Run by</th><th className="py-2">Status</th><th className="py-2 pr-5 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {runs.map((r) => (
                    <Fragment key={r.name}>
                      <tr className="border-b border-slate-50" data-testid={`payroll-run-${r.name}`}>
                        <td className="px-5 py-2">
                          <button onClick={() => toggleExpand(r)} className="flex items-center gap-1 hover:text-teal-700" data-testid={`payroll-run-expand-${r.name}`}>
                            {expanded?.name === r.name ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            <span className="font-medium text-slate-700">{r.date_from} → {r.date_to}</span>
                          </button>
                        </td>
                        <td className="py-2">{r.employee_count}</td>
                        <td className="py-2">{r.shift_count}</td>
                        <td className="py-2">{r.row_count}</td>
                        <td className="py-2 text-slate-500">{r.created_by_user}<div className="text-[10px] text-slate-400">{fmtLog(r.created_at)}</div></td>
                        <td className="py-2"><Badge variant="outline" className={`text-[10px] ${r.status === "Voided" ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-teal-50 text-teal-700 border-teal-200"}`}>{r.status}</Badge></td>
                        <td className="py-2 pr-5 text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={() => redownload(r)} data-testid={`payroll-run-download-${r.name}`}><Download className="h-3.5 w-3.5 mr-1" />CSV</Button>
                          <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={() => downloadExpenses(r)} title="Approved expense claims to enter manually in Pay Cat" data-testid={`payroll-run-expenses-${r.name}`}><Receipt className="h-3.5 w-3.5 mr-1" />Expenses</Button>
                          {r.status !== "Voided" && (
                            <Button variant="ghost" size="sm" className="h-7 text-[11px] text-red-600" onClick={() => { setVoidRun(r); setReason(""); }} data-testid={`payroll-run-void-${r.name}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Void</Button>
                          )}
                        </td>
                      </tr>
                      {expanded?.name === r.name && (
                        <tr className="bg-slate-50/60"><td colSpan={7} className="px-5 py-3">
                          {r.status === "Voided" && <div className="text-[10px] text-slate-500 mb-2">Voided by {expanded.voided_by_user} · {fmtLog(expanded.voided_at)} — “{expanded.void_reason}”</div>}
                          <div className="space-y-1" data-testid={`payroll-run-employees-${r.name}`}>
                            {(expanded.employees_json || []).map((e) => (
                              <div key={e.prof} className="flex items-center gap-3 text-xs py-1 border-b border-slate-100 last:border-0">
                                <span className={`font-medium min-w-[180px] ${e.voided ? "line-through text-slate-400" : "text-slate-700"}`}>{e.worker_name}</span>
                                <span className="text-slate-400 font-mono text-[10px]">{e.eh}</span>
                                <span className="text-slate-500">{e.shift_count} shift(s) · {e.row_count} row(s)</span>
                                <span className="ml-auto">
                                  {e.voided ? (
                                    <span className="text-[10px] text-slate-400" title={`Voided by ${e.voided_by || ""} — ${e.void_reason || ""}`}>Voided</span>
                                  ) : r.status !== "Voided" && (
                                    <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-600" onClick={() => { setVoidEmp({ run: r, employee: e }); setReason(""); }} data-testid={`payroll-emp-void-${e.prof}`}>Void</Button>
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3" data-testid={`payroll-run-expense-claims-${r.name}`}>
                            <div className="flex items-center gap-2 text-xs font-semibold text-slate-600 mb-1">
                              <Receipt className="h-3.5 w-3.5" style={{ color: TEAL }} /> Expense claims
                              <span className="font-normal text-slate-400">· not in the timesheet CSV, enter these in Pay Cat manually</span>
                            </div>
                            {!runExpenses ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                              : runExpenses.error ? <p className="text-[11px] text-red-600">Could not load expense claims.</p>
                              : runExpenses.claims.length === 0 ? <p className="text-[11px] text-slate-400">No approved expense claims in this run.</p>
                              : (
                                <table className="text-[11px] w-full"><thead><tr className="text-slate-400 text-left">
                                  <th className="pr-2">Employee</th><th className="pr-2">Emp ID</th><th className="pr-2">Date</th><th className="pr-2">Shift</th>
                                  <th className="pr-2">Participant</th><th className="pr-2">Reason</th><th className="pr-2 text-right">Amount</th><th className="pr-2">Charged to</th><th>Receipt</th></tr></thead>
                                  <tbody>
                                    {runExpenses.claims.map((c) => (
                                      <tr key={`${c.key}-${c.idx}`} className="text-slate-700 border-b border-slate-100">
                                        <td className="pr-2">{c.worker_name}</td><td className="pr-2 font-mono text-[10px] text-slate-500">{c.eh}</td>
                                        <td className="pr-2 tabular-nums">{c.shift_date ? dayjs(c.shift_date).format("DD/MM/YYYY") : ""}</td><td className="pr-2">{c.shift}</td>
                                        <td className="pr-2">{c.participant}</td><td className="pr-2">{c.reason}</td>
                                        <td className="pr-2 text-right tabular-nums">{money(c.amount)}</td><td className="pr-2 text-slate-500">{c.charged_to}</td>
                                        <td>{c.has_receipt ? <ReceiptThumb shiftName={c.key} idx={c.idx} name={c.receipt_name} size={28} /> : <span className="text-slate-400">None</span>}</td>
                                      </tr>
                                    ))}
                                    {runExpenses.workers.map((w) => (
                                      <tr key={w.eh + w.worker_name} className="text-slate-600 font-semibold">
                                        <td className="pr-2" colSpan={6}>{w.worker_name} total ({w.count})</td>
                                        <td className="pr-2 text-right tabular-nums">{money(w.total)}</td><td colSpan={2} />
                                      </tr>
                                    ))}
                                    <tr className="text-slate-800 font-bold border-t border-slate-200">
                                      <td className="pr-2" colSpan={6}>Run total</td><td className="pr-2 text-right tabular-nums">{money(runExpenses.total)}</td><td colSpan={2} />
                                    </tr>
                                  </tbody></table>
                              )}
                          </div>
                        </td></tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Employee IDs */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div><div className="text-sm font-semibold text-slate-800">Employee External IDs</div>
            <p className="text-[11px] text-slate-500 max-w-2xl">By default we export <b>this system's Employee ID</b> as the External ID — set that same value as the <b>External ID</b> on each employee in Employment Hero. If you'd rather use EH's own IDs, enter the <b>EH ID</b> here to override (it takes priority for that worker).</p></div>
          <div className="relative"><Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff" className="h-9 text-sm pl-8 w-56" data-testid="payroll-staff-search" /></div>
        </div>
        <div className="max-h-80 overflow-auto border border-slate-100 rounded-lg">
          <div className="grid grid-cols-[1fr_140px_160px] gap-3 px-3 py-2 bg-slate-50 border-b border-slate-100 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            <span>Staff</span><span>System ID (default)</span><span>EH ID override</span>
          </div>
          {staff.map((s) => (
            <div key={s.name} className="grid grid-cols-[1fr_140px_160px] gap-3 items-center px-3 py-2 border-b border-slate-50">
              <span className="text-sm text-slate-700 truncate">{s.display_name}</span>
              <span className="text-xs font-mono text-slate-500">{s.system_id}</span>
              <Input value={ids[s.employee || s.name] ?? ""} onChange={(e) => setIds((m) => ({ ...m, [s.employee || s.name]: e.target.value }))}
                placeholder="Optional EH ID" className="h-8 text-sm" data-testid={`payroll-ehid-${s.name}`} />
            </div>
          ))}
        </div>
        <div className="flex justify-end"><Button onClick={saveIds} disabled={saving} variant="outline" data-testid="payroll-save-ids">Save EH ID overrides</Button></div>
      </div>

      {/* Void run dialog */}
      <Dialog open={!!voidRun} onOpenChange={(o) => { if (!o) { setVoidRun(null); setReason(""); } }}>
        <DialogContent data-testid="payroll-void-run-dialog">
          <DialogHeader>
            <DialogTitle>Void pay run</DialogTitle>
            <DialogDescription>{voidRun && `${voidRun.date_from} → ${voidRun.date_to}`} · all {voidRun?.shift_count} shift(s) will reopen for re-export. This does not retract a CSV already sent to Employment Hero.</DialogDescription>
          </DialogHeader>
          <div><Label className="text-xs">Reason (required)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="mt-1 text-sm" placeholder="Why is this run being voided?" data-testid="payroll-void-run-reason" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setVoidRun(null); setReason(""); }}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={doVoidRun} data-testid="payroll-void-run-confirm">Void run</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void employee dialog */}
      <Dialog open={!!voidEmp} onOpenChange={(o) => { if (!o) { setVoidEmp(null); setReason(""); } }}>
        <DialogContent data-testid="payroll-void-emp-dialog">
          <DialogHeader>
            <DialogTitle>Void employee pay</DialogTitle>
            <DialogDescription>{voidEmp && `${voidEmp.employee.worker_name} — ${voidEmp.employee.shift_count} shift(s) will reopen for re-export.`}</DialogDescription>
          </DialogHeader>
          <div><Label className="text-xs">Reason (required)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="mt-1 text-sm" placeholder="Why is this employee's pay being voided?" data-testid="payroll-void-emp-reason" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setVoidEmp(null); setReason(""); }}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={doVoidEmp} data-testid="payroll-void-emp-confirm">Void employee</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
